// State-centric delivery memo flow (parallel to Armaan's file-centric one in
// contractorService.js). Contractor uploads only proof photos + invoice + notes;
// admin's getRichDetail derives the rest (activities, materials, snags, drawings)
// from project state. Endpoints live under /api/delivery-memo/* (no project/ prefix).
import { getApiData, postFormData } from "../utils/APIHelper";

const ENDPOINTS = {
  // Single-file upload that returns a Reno blob URL; reused from Armaan's
  // delivery-upload route since the storage logic is identical.
  UPLOAD: "api/project/delivery-upload",
  // Our own endpoints (registered in delivery-memo.routes.js).
  // creation-data returns only the CURRENTLY-ACTIVE scope's activities/
  // materials/drawings/snags (preview also surfaced V1 lineage after CRs).
  CREATION_DATA: (paymentGroupId) =>
    `api/delivery-memo/creation-data?payment_group_id=${encodeURIComponent(paymentGroupId)}`,
  // Scoped detail: active-scope + de-duplicated (like creation-data) so the
  // view screen matches the create screen. Falls back to the raw rich detail
  // shape (same keys) so no other change is needed.
  GET_BY_ID: (memoId) => `api/delivery-memo/${memoId}/detail`,
  CREATE: "api/delivery-memo",
  UPDATE: (memoId) => `api/delivery-memo/${memoId}`,
  ACTION: (memoId) => `api/delivery-memo/${memoId}/action`,
};

/**
 * Upload a single file to Reno blob storage, returns { url, path, mime_type }.
 * Used to gather proof URLs / invoice URL before POSTing the memo.
 */
export const uploadDeliveryFile = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const res = await postFormData(ENDPOINTS.UPLOAD, fd, {}, false, "POST");
  if (!res || !res.success || !res.url) {
    throw new Error(res?.message || "Failed to upload file.");
  }
  return { url: res.url, path: res.path, mime_type: res.mime_type };
};

/**
 * Creation data (active-scope derived sections) for a payment group BEFORE a
 * memo exists. Returns { payment_group, activities, materials, snags,
 * reference_drawings } so the contractor screen can populate on first render.
 */
export const getOurDeliveryMemoCreationData = async (paymentGroupId) => {
  const res = await getApiData(ENDPOINTS.CREATION_DATA(paymentGroupId), "GET", {}, undefined, false);
  if (!res?.success) {
    throw new Error(res?.message || "Failed to load delivery memo creation data.");
  }
  return res;
};

/**
 * Rich detail payload (activities, materials, snags, drawings, AI summary,
 * proof images, invoice link). Same endpoint admin portal consumes.
 */
export const getOurDeliveryMemoById = async (memoId) => {
  const res = await getApiData(ENDPOINTS.GET_BY_ID(memoId), "GET", {}, undefined, false);
  if (!res?.success) {
    throw new Error(res?.message || "Failed to load delivery memo.");
  }
  return res;
};

/**
 * Create a DRAFT memo. proofUrls is [{url, mime_type}], invoiceUrl is a single
 * blob URL. Backend assigns version, contractor_id from JWT.
 */
export const createOurDeliveryMemo = async ({
  paymentGroupId,
  invoiceUrl,
  proofUrls = [],
  notes = null,
}) => {
  const body = {
    payment_group_id: paymentGroupId,
    invoice_url: invoiceUrl,
    proof_urls: proofUrls,
    notes,
  };
  const res = await getApiData(ENDPOINTS.CREATE, "POST", body, {
    "Content-Type": "application/json",
  });
  if (!res?.success) {
    throw new Error(res?.message || "Failed to create delivery memo.");
  }
  return res;
};

/**
 * Patch a DRAFT memo. proofUrls (when supplied) REPLACES the current proof
 * set — caller sends the full current list. Pass `undefined` to leave a
 * field unchanged.
 */
export const updateOurDeliveryMemo = async (memoId, { invoiceUrl, proofUrls, notes }) => {
  const body = {};
  if (invoiceUrl !== undefined) body.invoice_url = invoiceUrl;
  if (proofUrls !== undefined) body.proof_urls = proofUrls;
  if (notes !== undefined) body.notes = notes;
  const res = await getApiData(ENDPOINTS.UPDATE(memoId), "PATCH", body, {
    "Content-Type": "application/json",
  });
  if (!res?.success) {
    throw new Error(res?.message || "Failed to update delivery memo.");
  }
  return res;
};

/**
 * Transition status (SUBMITTED / APPROVED / REJECTED / DISPUTED). Role gating
 * is enforced server-side. Contractor → SUBMITTED, homeowner → APPROVED / REJECTED / DISPUTED.
 */
export const actionOurDeliveryMemo = async (memoId, status, { disputeReason } = {}) => {
  const body = { status };
  if (disputeReason != null) body.dispute_reason = disputeReason;
  const res = await getApiData(ENDPOINTS.ACTION(memoId), "PUT", body, {
    "Content-Type": "application/json",
  });
  if (!res?.success) {
    throw new Error(res?.message || "Failed to update memo status.");
  }
  return res;
};
